<template>
    <v-dialog
        v-model="show"
        persistent
        eager
        :width="displayWidth"
        @keydown.esc="onEscapeKeydown"
    >
        <v-card role="region" aria-label="upload-document-dialog">
            <v-card-title class="px-4">
                {{ title }}
            </v-card-title>
            <v-card-text class="px-5">
                <v-container fluid pa-0 height="300px">
                    <v-row no-gutters>
                        <v-col class="pb-4">
                            Please keep in mind that the document file will be
                            uploaded to public cloud storage. It should not
                            contain any proprietary or confidential information.
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col class="pb-2">
                            <v-file-input
                                v-model="selectedFile"
                                dense
                                show-size
                                hide-details
                                outlined
                                accept=".pdf,.doc"
                                prepend-icon=""
                                :append-icon="icons.file"
                                placeholder="Click here to select a file"
                                aria-label="file-name"
                            >
                            </v-file-input>
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col>
                            <v-radio-group
                                v-model="visibility"
                                dense
                                hide-details
                                label="Visibility"
                                class="pl-2 mt-1"
                            >
                                <v-radio
                                    v-for="(item, index) in visibilityOptions"
                                    :key="`radio-${index}`"
                                    :label="item.text"
                                    :value="item.value"
                                >
                                </v-radio>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
            <v-card-actions class="justify-end px-4 pb-2">
                <v-btn outlined small @click="onCancelClick">Cancel</v-btn>
                <v-btn
                    color="primary"
                    small
                    class="ml-4"
                    :disabled="!allowUpload"
                    @click="onUploadClick"
                    >Upload</v-btn
                >
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { isEmpty } from '@/common/helpers';
import { mapState, mapActions } from 'vuex';

export default {
    name: 'UploadFileDialog',
    props: {
        show: {
            type: Boolean,
            required: true
        },
        title: {
            type: String,
            required: false,
            default: 'Upload new document'
        },
        width: {
            type: String,
            required: false,
            default: '500'
        }
    },
    data() {
        return {
            selectedFile: null,
            visibility: 'mso'
        };
    },
    computed: {
        ...mapState('authUser', ['userScopeOptions']),
        ...mapState('venue', ['selectedVenue']),
        ...mapState('support', ['prefixes']),
        displayWidth() {
            if (this.$vuetify.breakpoint.mdAndUp) {
                return this.width;
            }
            return '90%';
        },
        userVenues() {
            if (!isEmpty(this.userScopeOptions)) {
                return this.userScopeOptions.map(item => ({
                    text: item.venue.title,
                    value: item.venue.identifier
                }));
            }
            return [];
        },
        currentVenue() {
            if (!isEmpty(this.selectedVenue)) {
                return this.selectedVenue.venue;
            }
            return {};
        },
        currentPartner() {
            if (!isEmpty(this.selectedVenue)) {
                return this.selectedVenue.partner;
            }
            return {};
        },
        currentMso() {
            if (!isEmpty(this.selectedVenue)) {
                return this.selectedVenue.mso;
            }
            return {};
        },
        isFormValid() {
            if (this.selectedFile) {
                return !isEmpty(this.selectedFile.name);
            }
            return false;
        },
        visibilityOptions() {
            return [
                {
                    value: 'venue',
                    text: `visible to ${this.currentVenue.title} only`
                },
                {
                    value: 'partner',
                    text: `visible to all venues under ${this.currentPartner.identifier}`
                },
                { value: 'mso', text: 'visible to all venues / all partners' }
            ];
        },
        filePrefix() {
            if (this.prefixes && this.prefixes.length) {
                const scopePrefix = this.prefixes.find(item =>
                    item.venue.endsWith(`${this.currentVenue.identifier}/`)
                );
                if (scopePrefix) {
                    return scopePrefix[this.visibility];
                }
            }
            return '';
        },
        allowUpload() {
            return this.isFormValid && this.filePrefix;
        }
    },
    watch: {
        show(value) {
            if (value) {
                this.selectedFile = undefined;
            }
        }
    },
    methods: {
        ...mapActions('support', ['postDocument']),
        // ================== Events =====================
        onCancelClick() {
            this.$emit('close');
        },
        onUploadClick() {
            this.uploadDocument();
        },
        onEscapeKeydown() {
            this.onCancelClick();
        },
        // ================ Others ========================
        async uploadDocument() {
            if (this.selectedFile) {
                await this.postDocument({
                    document: this.selectedFile,
                    prefix: this.filePrefix
                });
            }
            this.$emit('close', true);
        }
    }
};
</script>

<style lang="scss" scoped></style>
